// 총판 데스크 셸 (distributor-desk) — 정제된 운영 콘솔 룩.
// 좌측 사이드바(브랜드 마크 + 아이콘 네비 + 인디고 액티브 필) + <Outlet/>.
// 데이터를 1회 로드해 하위 라우트에 Outlet context 로 주입. 0004 미적용/미설정도 degrade.
import { useEffect, useState } from "react";
import { NavLink, Outlet, useOutletContext } from "react-router-dom";
import {
  isSupabaseConfigured,
  countriesRepo,
  partnersRepo,
  productsRepo,
  contractsRepo,
  contractSalesRepo,
  dealsRepo,
  distributorRelationshipsRepo,
  distributorProvisionsRepo,
  distributorIssuesRepo,
  contractAmendmentsRepo,
} from "@/lib/db";
import type {
  Country,
  Partner,
  Product,
  ContractRow,
  ContractSale,
  Deal,
  DistributorRelationship,
  DistributorProvision,
  DistributorIssue,
  ContractAmendment,
} from "@/types";
import { cx, IconBoard, IconUsers, IconFolder, IconSpark, IconDoc, IconFlag } from "./ui";

export interface DistributorDeskData {
  loading: boolean;
  configured: boolean;
  countries: Country[];
  partners: Partner[];
  products: Product[];
  contracts: ContractRow[];
  sales: ContractSale[];
  deals: Deal[];
  relationships: DistributorRelationship[];
  provisions: DistributorProvision[];
  issues: DistributorIssue[];
  amendments: ContractAmendment[];
}

const EMPTY: DistributorDeskData = {
  loading: true,
  configured: true,
  countries: [],
  partners: [],
  products: [],
  contracts: [],
  sales: [],
  deals: [],
  relationships: [],
  provisions: [],
  issues: [],
  amendments: [],
};

/** 개별 로드 실패(테이블 미존재 등)를 빈 배열로 흡수 — 보드가 부분 degrade. */
async function safe<T>(p: Promise<T[]>): Promise<T[]> {
  try {
    return await p;
  } catch {
    return [];
  }
}

type IconCmp = (p: { className?: string }) => JSX.Element;
const NAV: { to: string; label: string; icon: IconCmp; end?: boolean }[] = [
  { to: "/distributor", label: "국가별 현황", icon: IconBoard, end: true },
  { to: "/distributor/relationships", label: "총판·관계", icon: IconUsers },
  { to: "/distributor/materials", label: "자료 공유", icon: IconFolder },
  { to: "/distributor/marketing", label: "마케팅·이벤트", icon: IconSpark },
  { to: "/distributor/contracts", label: "계약 변경", icon: IconDoc },
  { to: "/distributor/issues", label: "이슈", icon: IconFlag },
];

export function DistributorDeskLayout(): JSX.Element {
  const [data, setData] = useState<DistributorDeskData>(EMPTY);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isSupabaseConfigured()) {
        if (alive) setData({ ...EMPTY, loading: false, configured: false });
        return;
      }
      const [
        countries,
        partners,
        products,
        contracts,
        sales,
        deals,
        relationships,
        provisions,
        issues,
        amendments,
      ] = await Promise.all([
        safe(countriesRepo.list()),
        safe(partnersRepo.list()),
        safe(productsRepo.list()),
        safe(contractsRepo.list()),
        safe(contractSalesRepo.list()),
        safe(dealsRepo.list()),
        safe(distributorRelationshipsRepo.list()),
        safe(distributorProvisionsRepo.list()),
        safe(distributorIssuesRepo.list()),
        safe(contractAmendmentsRepo.list()),
      ]);
      if (!alive) return;
      setData({
        loading: false,
        configured: true,
        countries,
        partners,
        products,
        contracts,
        sales,
        deals,
        relationships,
        provisions,
        issues,
        amendments,
      });
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="dist-shell flex min-h-[calc(100vh-48px)] bg-[#f8fafc] text-slate-800">
      <aside className="sticky top-0 hidden h-[calc(100vh-48px)] w-60 shrink-0 flex-col border-r border-slate-200/70 bg-white/70 backdrop-blur-sm md:flex">
        <div className="flex items-center gap-2.5 px-4 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-[13px] font-bold text-white shadow-sm">
            총판
          </div>
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-slate-900">총판 데스크</div>
            <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
              Distributor Control
            </div>
          </div>
        </div>

        <nav className="flex flex-col gap-0.5 px-2.5 py-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cx(
                  "group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition",
                  isActive
                    ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-100"
                    : "text-slate-500 hover:bg-slate-100/70 hover:text-slate-800",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={cx("h-[18px] w-[18px]", isActive ? "text-indigo-500" : "text-slate-400 group-hover:text-slate-500")} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto border-t border-slate-200/60 px-4 py-3">
          <div className="text-[11px] leading-relaxed text-slate-400">
            본사 내부용 · 계약가·마진 등 대외비는 CF Access 연결 이후 적재하세요.
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <div className="mx-auto max-w-[1240px] px-5 py-6 sm:px-8">
          {/* 모바일 상단 탭 (사이드바 숨김 시) */}
          <nav className="mb-4 flex gap-1 overflow-x-auto md:hidden">
            {NAV.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cx(
                    "whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[12px] font-medium",
                    isActive ? "bg-indigo-600 text-white" : "bg-white text-slate-500 ring-1 ring-slate-200",
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          {!data.configured && (
            <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-[13px] text-amber-800">
              <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-amber-400" />
              <span>
                Supabase가 설정되지 않아 읽기 전용 빈 화면입니다. <code className="rounded bg-amber-100 px-1">.env</code>를
                설정하면 데이터가 로드됩니다.
              </span>
            </div>
          )}
          <Outlet context={data} />
        </div>
      </main>
    </div>
  );
}

/** 하위 라우트에서 총판 데스크 로드 데이터를 읽는다. */
export function useDistributorData(): DistributorDeskData {
  return useOutletContext<DistributorDeskData>();
}
